// ============================================================
// v8 — the community card sandbox (BOLT-009).
//
// Untrusted card scripts run in a QuickJS-WASM context with NO I/O
// (no fetch/require/fs/process — a bare JS engine), a memory cap, an
// instruction-budget interrupt (no infinite loops), and Date/
// Math.random removed (determinism via the seeded ctx.rng / geo.rng).
//
// THE PRIVACY + LEAKAGE FIREWALL: the script computes with `H` but
// can never exfiltrate it (no I/O), and the only geometry it can
// produce is via the trusted `geo.*` constructors, which return an
// OPAQUE integer region id. The host owns the actual polygons; the
// guest can only reference ids. A card returns { op, region:id }
// (or an array) and the HOST applies the clip to Z through the same
// guarded engine path as the built-ins (engine.clipZone). Raw
// geometry in the return value is rejected.
//
// The WASM module is loaded ONCE (loadCardSandbox) and cached, so
// runCardScriptSync is synchronous — it drops into the existing
// synchronous resolution path (session.ts) and the browser studio
// (BOLT-012) without an async refactor.
// ============================================================
import {
  DefaultIntrinsics,
  newQuickJSWASMModuleFromVariant,
  shouldInterruptAfterDeadline,
  type QuickJSContext,
  type QuickJSHandle,
  type QuickJSWASMModule,
} from 'quickjs-emscripten-core';
// Single-file variant: the WASM is embedded as base64, so there is no separate
// .wasm fetch — it bundles + runs identically in Node (server/tests) and the
// browser (the studio). Works in both per the v8 sandbox design.
import quickJsVariant from '@jitl/quickjs-singlefile-browser-release-sync';
import {
  area,
  bearing as turfBearing,
  circle,
  destination,
  difference as turfDifference,
  featureCollection,
  intersect as turfIntersect,
  point as turfPoint,
  polygon as turfPolygon,
  union as turfUnion,
  distance as turfDistance,
} from '@turf/turf';
import type { Feature, Polygon, MultiPolygon, Position } from 'geojson';
import { buildHalfPlane, clipZone } from '../engine';
import { METERS_PER_FOOT } from '../constants';
import { mulberry32 } from '../rng';
import type { LngLat, Zone } from '../protocol';

type Poly = Feature<Polygon | MultiPolygon>;

// ---------------- module load (once) ----------------
let modPromise: Promise<QuickJSWASMModule> | null = null;
let cached: QuickJSWASMModule | null = null;

/** Preload the QuickJS-WASM module so runCardScriptSync can be synchronous.
 *  Call once at server startup and in test setup. Idempotent. */
export async function loadCardSandbox(): Promise<void> {
  if (!modPromise) modPromise = newQuickJSWASMModuleFromVariant(quickJsVariant);
  cached = await modPromise;
}
export function isCardSandboxReady(): boolean {
  return cached !== null;
}

// ---------------- run ----------------
export interface CardScriptContext {
  S: LngLat;
  H: LngLat;
  Z: Zone;
  boundary?: Zone | null;
  now?: number;
  seed?: number;
  walk?: { start: LngLat; end: LngLat };
  roads?: LngLat[][]; // layers.roads()
}

export interface CardRunResult {
  ok: boolean;
  zone?: Zone; // the new clipped zone, if ok
  cutPct?: number; // % of Z eliminated
  ops?: Array<{ op: 'keep' | 'remove'; region: number }>; // what the card returned (trace)
  regionCount?: number; // regions the card constructed (trace)
  error?: string;
}

export interface RunOptions {
  memoryBytes?: number;
  deadlineMs?: number;
}
const DEFAULT_MEMORY = 16 * 1024 * 1024; // 16 MB
const DEFAULT_DEADLINE_MS = 250; // wall-clock instruction budget

// ---------------- host geometry helpers ----------------
const pt = (p: LngLat) => turfPoint([p.lng, p.lat]);
const distM = (a: LngLat, b: LngLat) => turfDistance(pt(a), pt(b), { units: 'meters' });
const HUGE_M = 8000;

function diskPoly(center: LngLat, radiusM: number): Poly {
  return circle(pt(center), Math.max(0, radiusM), { units: 'meters', steps: 96 }) as Feature<Polygon>;
}
function diffPoly(a: Poly, b: Poly): Poly {
  const r = turfDifference(featureCollection([a, b]));
  return (r as Poly | null) ?? a;
}
function unionPolys(polys: Poly[]): Poly {
  if (polys.length === 0) throw new Error('union of no regions');
  if (polys.length === 1) return polys[0];
  const r = turfUnion(featureCollection(polys as Feature<Polygon | MultiPolygon>[]));
  return (r as Poly | null) ?? polys[0];
}
function intersectPolys(polys: Poly[]): Poly {
  if (polys.length === 0) throw new Error('intersect of no regions');
  let acc = polys[0];
  for (let i = 1; i < polys.length; i++) {
    const r = turfIntersect(featureCollection([acc, polys[i]]));
    if (!r) throw new Error('empty intersection');
    acc = r as Poly;
  }
  return acc;
}
/** A 90° (or arbitrary) wedge from a centre spanning fromDeg→toDeg, as a huge polygon. */
function wedgePoly(center: LngLat, fromDeg: number, toDeg: number): Poly {
  const span = ((toDeg - fromDeg) % 360 + 360) % 360 || 360;
  const steps = Math.max(2, Math.ceil(span / 5));
  const ring: Position[] = [[center.lng, center.lat]];
  for (let i = 0; i <= steps; i++) {
    const brng = fromDeg + (span * i) / steps;
    ring.push(destination(pt(center), HUGE_M, brng, { units: 'meters' }).geometry.coordinates as Position);
  }
  ring.push([center.lng, center.lat]);
  return turfPolygon([ring]);
}
function nearestPoint(points: LngLat[], to: LngLat): LngLat {
  let best = points[0];
  let bestD = Infinity;
  for (const p of points) {
    const d = distM(p, to);
    if (d < bestD) {
      bestD = d;
      best = p;
    }
  }
  return best;
}

// ---------------- guest marshaling ----------------
function setLngLat(vm: QuickJSContext, parent: QuickJSHandle, key: string, p: LngLat): void {
  const obj = vm.newObject();
  const lat = vm.newNumber(p.lat);
  vm.setProp(obj, 'lat', lat);
  lat.dispose();
  const lng = vm.newNumber(p.lng);
  vm.setProp(obj, 'lng', lng);
  lng.dispose();
  vm.setProp(parent, key, obj);
  obj.dispose();
}
function lngLatHandle(vm: QuickJSContext, p: LngLat): QuickJSHandle {
  const obj = vm.newObject();
  const lat = vm.newNumber(p.lat);
  vm.setProp(obj, 'lat', lat);
  lat.dispose();
  const lng = vm.newNumber(p.lng);
  vm.setProp(obj, 'lng', lng);
  lng.dispose();
  return obj;
}

function normalizeOps(ret: unknown, regionCount: number): Array<{ op: 'keep' | 'remove'; region: number }> | null {
  const arr = Array.isArray(ret) ? ret : [ret];
  const out: Array<{ op: 'keep' | 'remove'; region: number }> = [];
  for (const item of arr) {
    if (!item || typeof item !== 'object') return null;
    const op = (item as { op?: unknown }).op;
    const region = (item as { region?: unknown }).region;
    if (op !== 'keep' && op !== 'remove') return null;
    // OPAQUE: region must be a valid integer id (raw geometry is rejected here).
    if (typeof region !== 'number' || !Number.isInteger(region) || region < 0 || region >= regionCount) return null;
    out.push({ op, region });
  }
  return out.length ? out : null;
}

/** Run an untrusted card script synchronously (the module must be preloaded). */
export function runCardScriptSync(source: string, ctx: CardScriptContext, opts: RunOptions = {}): CardRunResult {
  if (!cached) throw new Error('card sandbox not loaded — call loadCardSandbox() first');

  const runtime = cached.newRuntime();
  runtime.setMemoryLimit(opts.memoryBytes ?? DEFAULT_MEMORY);
  runtime.setMaxStackSize(512 * 1024);
  runtime.setInterruptHandler(shouldInterruptAfterDeadline(Date.now() + (opts.deadlineMs ?? DEFAULT_DEADLINE_MS)));
  const vm = runtime.newContext({ intrinsics: { ...DefaultIntrinsics, Date: false } });

  const regions: Poly[] = [];
  const reg = (f: Poly): number => regions.push(f) - 1;
  const rng = mulberry32(((ctx.seed ?? 1) >>> 0) || 1);

  try {
    installGeo(vm, ctx, regions, reg, rng);
    // belt-and-suspenders determinism: no ambient Date/Math.random in the guest
    const prelude = vm.evalCode('globalThis.Math.random = undefined; void 0;');
    if (prelude.error) prelude.error.dispose();
    else prelude.value.dispose();

    const ctxHandle = buildCtxHandle(vm, ctx);
    vm.setProp(vm.global, '__ctx', ctxHandle);
    ctxHandle.dispose();

    const wrapped = `(function(ctx){\n"use strict";\n${source}\n})(__ctx)`;
    const res = vm.evalCode(wrapped);
    if (res.error) {
      const dumped = vm.dump(res.error);
      res.error.dispose();
      const msg = dumped && typeof dumped === 'object' && 'message' in dumped ? String((dumped as { message: unknown }).message) : String(dumped);
      return { ok: false, error: msg || 'script error', regionCount: regions.length };
    }
    const ret = vm.dump(res.value);
    res.value.dispose();

    const ops = normalizeOps(ret, regions.length);
    if (!ops) {
      return {
        ok: false,
        error: 'card must return { op: "keep"|"remove", region: <id from geo.*> } (or an array). Raw geometry is not allowed.',
        regionCount: regions.length,
      };
    }
    let Z = ctx.Z as Poly;
    const before = area(Z);
    for (const o of ops) Z = clipZone(Z, regions[o.region], o.op);
    const after = area(Z);
    return {
      ok: true,
      zone: Z as Zone,
      cutPct: before > 0 ? Math.max(0, Math.min(100, ((before - after) / before) * 100)) : 0,
      ops,
      regionCount: regions.length,
    };
  } catch (e) {
    return { ok: false, error: e instanceof Error ? e.message : String(e), regionCount: regions.length };
  } finally {
    vm.dispose();
    runtime.dispose();
  }
}

function buildCtxHandle(vm: QuickJSContext, ctx: CardScriptContext): QuickJSHandle {
  const o = vm.newObject();
  setLngLat(vm, o, 'S', ctx.S);
  setLngLat(vm, o, 'H', ctx.H);
  const now = vm.newNumber(ctx.now ?? 0);
  vm.setProp(o, 'now', now);
  now.dispose();
  const seed = vm.newNumber(ctx.seed ?? 1);
  vm.setProp(o, 'seed', seed);
  seed.dispose();
  if (ctx.walk) {
    const w = vm.newObject();
    setLngLat(vm, w, 'start', ctx.walk.start);
    setLngLat(vm, w, 'end', ctx.walk.end);
    vm.setProp(o, 'walk', w);
    w.dispose();
  }
  return o;
}

/** Install the trusted geo/layers API. Region constructors return OPAQUE ids. */
function installGeo(
  vm: QuickJSContext,
  ctx: CardScriptContext,
  regions: Poly[],
  reg: (f: Poly) => number,
  rng: () => number,
): void {
  const geo = vm.newObject();
  const def = (name: string, fn: (...args: QuickJSHandle[]) => QuickJSHandle | void): void => {
    const f = vm.newFunction(name, fn);
    vm.setProp(geo, name, f);
    f.dispose();
  };
  const region = (i: QuickJSHandle): Poly => {
    const id = vm.getNumber(i);
    const r = regions[id];
    if (!r) throw new Error(`unknown region id ${id}`);
    return r;
  };

  def('feet', (a) => vm.newNumber(vm.getNumber(a) * METERS_PER_FOOT));
  def('distance', (a, b) => vm.newNumber(distM(vm.dump(a) as LngLat, vm.dump(b) as LngLat)));
  def('bearing', (a, b) => vm.newNumber(turfBearing(pt(vm.dump(a) as LngLat), pt(vm.dump(b) as LngLat))));
  def('disk', (c, r) => vm.newNumber(reg(diskPoly(vm.dump(c) as LngLat, vm.getNumber(r)))));
  def('annulus', (c, inner, outer) => {
    const ctr = vm.dump(c) as LngLat;
    return vm.newNumber(reg(diffPoly(diskPoly(ctr, vm.getNumber(outer)), diskPoly(ctr, vm.getNumber(inner)))));
  });
  def('wedge', (c, from, to) => vm.newNumber(reg(wedgePoly(vm.dump(c) as LngLat, vm.getNumber(from), vm.getNumber(to)))));
  def('halfPlaneBisector', (a, b) => vm.newNumber(reg(buildHalfPlane(vm.dump(a) as LngLat, vm.dump(b) as LngLat))));
  def('difference', (a, b) => vm.newNumber(reg(diffPoly(region(a), region(b)))));
  def('union', (idsArr) => {
    const ids = vm.dump(idsArr) as number[];
    return vm.newNumber(reg(unionPolys(ids.map((i) => regions[i]))));
  });
  def('intersect', (idsArr) => {
    const ids = vm.dump(idsArr) as number[];
    return vm.newNumber(reg(intersectPolys(ids.map((i) => regions[i]))));
  });
  def('nearest', (ptsArr, to) => lngLatHandle(vm, nearestPoint(vm.dump(ptsArr) as LngLat[], vm.dump(to) as LngLat)));
  def('rng', () => vm.newNumber(rng()));

  // layers.roads() — the only map layer wired in v8 (POIs/regions: spec §16).
  const layers = vm.newObject();
  const roadsFn = vm.newFunction('roads', () => {
    const arr = JSON.stringify(ctx.roads ?? []);
    const parsed = vm.evalCode(`(${arr})`);
    return vm.unwrapResult(parsed);
  });
  vm.setProp(layers, 'roads', roadsFn);
  roadsFn.dispose();

  vm.setProp(vm.global, 'geo', geo);
  vm.setProp(vm.global, 'layers', layers);
  geo.dispose();
  layers.dispose();
}
